/**
 * Converts a snake_case or camelCase column name to a human-readable display name.
 * Examples:
 *   "demand_type"    → "Demand Type"
 *   "so_id"          → "SO Id"
 *   "modified_date"  → "Modified Date"
 *   "coe_confidence" → "COE Confidence"
 *   "identified_assoc_id_external_candidate_id" → "Identified Assoc Id External Candidate Id"
 *   "eff_month"      → "Eff Month"
 *
 * Special abbreviations (SO, ID, COE, EIPO, PDL, EDL, HR, etc.) are kept uppercase.
 */

// Words that should always be fully uppercased
const UPPERCASE_WORDS = new Set([
  "so", "id", "coe", "eipo", "pdl", "edl", "hr", "bu", "mu", "rr",
  "tat", "sbu", "rsc", "rlc", "na", "ggm", "cca", "oe", "tsc", "sef",
]);

/**
 * Convert a snake_case or raw DB column name to a readable Title Case display name.
 * @param {string} fieldName - The raw field/column name (e.g. "demand_type", "so_id")
 * @returns {string} The display-friendly name (e.g. "Demand Type", "SO Id")
 */
export function toDisplayName(fieldName) {
  if (!fieldName || typeof fieldName !== "string") return fieldName || "";

  // If already looks like a display name (contains spaces and starts with uppercase), return as-is
  if (/^[A-Z]/.test(fieldName) && fieldName.includes(" ")) return fieldName;

  // Split on underscores, hyphens, or camelCase boundaries
  const words = fieldName
    .replace(/([a-z])([A-Z])/g, "$1_$2") // camelCase → snake_case
    .split(/[_\-\s]+/)
    .filter(Boolean);

  return words
    .map((word) => {
      const lower = word.toLowerCase();
      if (UPPERCASE_WORDS.has(lower)) return word.toUpperCase();
      // Capitalize first letter
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
}

export default toDisplayName;
